import React, { useMemo } from 'react';
import { getIntl } from '../../../../utils/intl';
import useNotes, { NoteQueryArgs } from '../../../../hooks/api/useNotes';
import ListSection, { MAX_CARD_COUNT } from './ListSection';
import LoadingContent from './LoadingContent';
import NoteCard from '../../../shared/NoteCard';
import Stack, { contentStackSpacing } from '../../Stack';
import { useProfilesTabNavigation } from '../../../../utils/appNavigationNames';
import { useMaybeRenderedSideDrawerToggle } from '../../SideDrawer/MaybeRenderedSideDrawer';
import SharedNotesList from '../../../shared/Notes/NotesList';
import AddNoteContent from './AddNoteContent';

const intl = getIntl('components.screens.tabs.profiles.profileActivityScreen.desktopRecentActivitySection.notesList', {
    noNotes: 'No Notes',
    notes: 'Notes',
    profileNotes: 'Profile Notes',
    recentNotes: 'Recent Notes',
    addNote: 'Add Note',
});

export type NotesListOwnProps = {
    profileId: string;
};

export type NotesListProps = NotesListOwnProps;

const NotesList: React.FC<NotesListProps> = (props) => {
    const { navigate } = useProfilesTabNavigation();
    const noteQueryArgs: NoteQueryArgs = useMemo(
        () => ({
            medicalProfileId: props.profileId,
            entityType: 'medical-profile',
        }),
        [props.profileId],
    );
    const { notes, isLoading } = useNotes({
        noteQueryArgs,
    });

    const sortedNotes = React.useMemo(() => {
        return notes.notes.sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
    }, [notes]);

    const notesListSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'profileNotes',
        title: intl('profileNotes'),
        renderContent: (onClose) => (
            <SharedNotesList
                noteQueryArgs={noteQueryArgs}
                onAddNote={(noteQueryArgs) => {
                    onClose(true);
                    navigate('add-note', noteQueryArgs);
                }}
                onViewDetails={(note) => {
                    onClose(true);
                    navigate('note', {
                        noteId: note.noteId,
                        noteEntityType: note.entityType,
                        profileId: props.profileId,
                    });
                }}
                flat
            />
        ),
        mobileOnFocus: () => {
            // no-op - shouldn't be rendered on mobile
            return;
        },
    });

    const addNoteSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addNote',
        title: intl('addNote'),
        renderContent: (onClose) => <AddNoteContent profileId={props.profileId} onClose={onClose} />,
        mobileOnFocus: () => {
            // no-op - shouldn't be rendered on mobile
            return;
        },
    });

    return (
        <>
            <ListSection
                children={
                    isLoading ? (
                        <LoadingContent />
                    ) : (
                        <Stack vertical space={contentStackSpacing}>
                            {sortedNotes.slice(0, MAX_CARD_COUNT).map((note) => {
                                return (
                                    <NoteCard
                                        key={note.noteId}
                                        note={note}
                                        onPress={() => {
                                            navigate('note', {
                                                noteId: note.noteId,
                                                noteEntityType: note.entityType,
                                                profileId: props.profileId,
                                            });
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    )
                }
                title={intl('recentNotes')}
                icon={'Edit2'}
                onPressSeeAll={() => {
                    notesListSideDrawer.onOpen();
                }}
                noResultsMessage={intl('noNotes')}
                showNoResultsMessage={!sortedNotes.length && !isLoading}
                seeAllTestId="see-all-notes"
                onAdd={addNoteSideDrawer.onOpen}
                addTestId="add-note-from-list"
            />
            {notesListSideDrawer.SideDrawer}
            {addNoteSideDrawer.SideDrawer}
        </>
    );
};

export default React.memo(NotesList);
